import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onBeforeUnmount, onMounted, ref } from "vue";
import * as SockJS from "sockjs-client";
import * as Stomp from "stompjs";
import { createToastInterface, POSITION } from "vue-toastification";
import { useStore } from "vuex";
import { getLocale } from "@/i18n";
import {
  HELPDESK_REALTIME_CONNECTED_EVENT,
  HELPDESK_REALTIME_EVENT,
  HelpdeskRealtimeEvent,
} from "@/services/helpdesk/HelpdeskRealtime";
import { AuthGetters, getAccountSessionToken } from "@/store/modules/auth";
import { accountKey, accountSessionMatches } from "@/utils/accountSession";
import { resolveLocalizedText } from "@/utils/localization";
import { NotificationActions } from "@/store/modules/notification/actions";
import { NotificationGetters } from "@/store/modules/notification/getters";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const connected = ref(false);
const newToast = createToastInterface({
  timeout: 5000,
  position: POSITION.BOTTOM_RIGHT,
});
const receivedHelpdeskEvents = new Set<string>();
const RECONNECT_MIN_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
let stompClient: any = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
let disposed = false;
let hasConnectedBefore = false;
let connecting = false;
let reconnectAttempt = 0;

onMounted(() => {
  window.addEventListener("online", reconnectNow);
  refreshNotificationCount();
  connect();
});
onBeforeUnmount(() => {
  window.removeEventListener("online", reconnectNow);
  disconnect();
});

function connect() {
  const identity = store.getters[AuthGetters.GET_USER_IDENTITY];
  let token: string | undefined;
  try {
    token = getAccountSessionToken(identity);
  } catch {
    return;
  }
  const notificationApi = process.env.VUE_APP_NOTIFICATION_API;
  if (
    !token ||
    !notificationApi ||
    disposed ||
    connected.value ||
    connecting ||
    navigator.onLine === false
  ) {
    return;
  }

  connecting = true;
  const socketUrl = `${notificationApi.replace(/\/$/, "")}/ws`;
  const client = Stomp.over(new SockJS(socketUrl));
  stompClient = client;
  client.debug = () => undefined;
  client.heartbeat.outgoing = 10000;
  client.heartbeat.incoming = 10000;
  connectTimeoutTimer = setTimeout(() => {
    if (stompClient !== client || !connecting) return;
    disconnectClient();
    scheduleReconnect();
  }, 15000);
  client.connect(
    { Authorization: `Bearer ${token}` },
    () => {
      if (stompClient !== client) return;
      clearConnectTimeout();
      connecting = false;
      connected.value = true;
      reconnectAttempt = 0;
      if (
        !identity?.id ||
        accountKey(identity) !==
          accountKey(store.getters[AuthGetters.GET_USER_IDENTITY]) ||
        !accountSessionMatches(identity)
      ) {
        disconnectClient();
        scheduleReconnect();
        return;
      }

      subscribeToNotifications();
      subscribeToHelpdesk(
        Boolean(identity.permissions?.includes("HELPDESK_MANAGE")),
        identity.tenant?.id ?? null
      );
      if (hasConnectedBefore) {
        window.dispatchEvent(new Event(HELPDESK_REALTIME_CONNECTED_EVENT));
      }
      hasConnectedBefore = true;
      refreshNotificationCount();
    },
    () => {
      if (stompClient !== client) return;
      clearConnectTimeout();
      connecting = false;
      connected.value = false;
      stompClient = null;
      scheduleReconnect();
    }
  );
}

function refreshNotificationCount() {
  void store
    .dispatch(NotificationActions.FETCH_NOTIFICATION_COUNT)
    .catch(() => undefined);
}

function subscribeToNotifications() {
  const client = stompClient;
  const identity = store.getters[AuthGetters.GET_USER_IDENTITY];
  const owner = accountKey(identity);
  client.subscribe("/user/queue/notification", (tick: any) => {
    if (
      disposed ||
      stompClient !== client ||
      owner !== accountKey(store.getters[AuthGetters.GET_USER_IDENTITY]) ||
      !accountSessionMatches(identity)
    )
      return;
    const parsedTick = parseJson(tick.body);
    if (
      !parsedTick ||
      !["NOTIFICATION", "SYSTEM", "PROCESS"].includes(parsedTick.type)
    )
      return;

    const previousCount = Number(
      store.getters[NotificationGetters.GET_NOTIFICATION_COUNT] || 0
    );
    const snapshotCount = parsedTick.counterInfo?.notificationCount;
    let nextCount: number | null = null;
    if (Number.isSafeInteger(snapshotCount) && snapshotCount >= 0) {
      nextCount = snapshotCount;
    } else if (parsedTick.type !== "PROCESS") {
      // Keep compatibility with older servers that send deltas rather than snapshots.
      const change = parsedTick.notificationInfo;
      if (
        Number.isSafeInteger(change?.notificationCount) &&
        change.notificationCount >= 0
      ) {
        if (change.changeType === "INCREASE")
          nextCount = previousCount + change.notificationCount;
        if (change.changeType === "DECREASE")
          nextCount = Math.max(0, previousCount - change.notificationCount);
      }
    }
    if (nextCount !== null && Number.isSafeInteger(nextCount)) {
      void store.dispatch(
        NotificationActions.SET_NOTIFICATION_COUNT,
        nextCount
      );
      if (
        parsedTick.type !== "PROCESS" &&
        nextCount > previousCount &&
        parsedTick.message
      ) {
        newToast.info(resolveLocalizedText(String(parsedTick.message)));
      }
    }
    if (parsedTick.counterInfo) {
      void store.dispatch(
        NotificationActions.SET_PROCESS_INFO_NOTIFICATION,
        parsedTick.counterInfo
      );
    }
  });
}

function subscribeToHelpdesk(isManager: boolean, tenantId: string | null) {
  stompClient.subscribe("/user/queue/helpdesk", handleHelpdeskMessage);
  if (isManager) {
    const destination = tenantId == null
      ? "/topic/helpdesk/legacy"
      : `/topic/helpdesk/tenant/${tenantId}`;
    stompClient.subscribe(destination, handleHelpdeskMessage);
  }
}

function handleHelpdeskMessage(tick: any) {
  const event = parseJson(tick.body) as HelpdeskRealtimeEvent | null;
  const identity = store.getters[AuthGetters.GET_USER_IDENTITY];
  if (
    !event?.eventId ||
    !event.ticketId ||
    !event.ticketNumber ||
    (event.tenantId ?? null) !== (identity?.tenant?.id ?? null) ||
    receivedHelpdeskEvents.has(event.eventId)
  ) {
    return;
  }

  receivedHelpdeskEvents.add(event.eventId);
  if (receivedHelpdeskEvents.size > 500) {
    const oldest = receivedHelpdeskEvents.values().next().value;
    if (oldest) receivedHelpdeskEvents.delete(oldest);
  }

  window.dispatchEvent(
    new CustomEvent<HelpdeskRealtimeEvent>(HELPDESK_REALTIME_EVENT, {
      detail: event,
    })
  );
  if (event.actorId !== identity?.id) {
    newToast.info(`${helpdeskToastPrefix()} ${event.ticketNumber}`);
  }
}

function helpdeskToastPrefix() {
  const locale = getLocale();
  if (locale === "ru") return "Обновлено обращение";
  if (locale === "kk") return "Өтініш жаңартылды";
  return "Ticket updated";
}

function parseJson(value: string): any | null {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function scheduleReconnect() {
  if (disposed || reconnectTimer || navigator.onLine === false) return;
  const baseDelay = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_MIN_DELAY_MS * 2 ** reconnectAttempt
  );
  const jitter = Math.floor(Math.random() * Math.max(1, baseDelay * 0.2));
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, baseDelay + jitter);
}

function reconnectNow() {
  if (disposed || connected.value || connecting) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  reconnectAttempt = 0;
  connect();
}

function disconnectClient() {
  clearConnectTimeout();
  const client = stompClient;
  stompClient = null;
  connecting = false;
  connected.value = false;
  if (client?.connected) {
    client.disconnect(() => undefined);
  } else if (client?.ws?.readyState < 2) {
    client.ws.close();
  }
}

function clearConnectTimeout() {
  if (connectTimeoutTimer) clearTimeout(connectTimeoutTimer);
  connectTimeoutTimer = null;
}

function disconnect() {
  disposed = true;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  disconnectClient();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div"))
}
}

})